// src/pages/reports/ReportsFilterPopup.tsx
import React, {
  Dispatch,
  SetStateAction,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Checkbox,
  CircularProgress,
  InputAdornment,
  GlobalStyles,
} from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getToken } from "../../../utils/utils";
import { getalllocations, getalloutlettype } from "../../assign-task/api";
import { getReportsFilterOptions } from "../api";
// same assets used in ReportsLogFilter
import arrowLeft from "../../../assests/images/arrow-left.svg";
import arrowRight from "../../../assests/images/arrow-right.png";
import doubleArrowLeft from "../../../assests/images/double-arrow-left.svg";
import doubleArrowRight from "../../../assests/images/double-arrow-right.svg";

const CLEAR_X_DATA_URI =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"><path fill="#888" d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59L7.11 5.7A1 1 0 0 0 5.7 7.11L10.59 12l-4.9 4.89a1 1 0 1 0 1.41 1.42L12 13.41l4.89 4.9a1 1 0 0 0 1.42-1.42L13.41 12l4.9-4.89a1 1 0 0 0-.01-1.4Z"/></svg>`
  );

// 🔹 array equality (order-sensitive) - used only where needed
const arrEq = (a: string[], b: string[]) => {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
};

// 🔹 Custom arrow to match Rewards UI
const CustomArrow = ({
  open,
  onClick,
}: {
  open?: boolean;
  onClick?: () => void;
}) => (
  <span
    onClick={(e) => {
      e.stopPropagation();
      onClick?.();
    }}
    style={{
      display: "inline-block",
      width: 10,
      height: 10,
      borderRight: "2px solid #222",
      borderBottom: "2px solid #222",
      transform: open ? "rotate(-135deg)" : "rotate(45deg)",
      transformOrigin: "center center",
      transition: "transform 0.18s ease",
      marginLeft: 12,
      position: "relative",
      cursor: "pointer",
      boxSizing: "border-box",
    }}
  />
);

// add to ReportsFilterPayload
export interface ReportsFilterPayload {
  locations?: string[] | null;
  outletNames?: string[] | null;
  pmi_entity_code?: string[] | null;
  status?: string[] | null;
  user_salesforce_id?: string[] | null;
  contract_since_from?: string | null;
  contract_since_to?: string | null;
  lastloginStart?: string | null;
  lastloginEnd?: string | null;
  backendFilters?: {
    outlet_city?: string[] | null;
    outlet_category?: string[] | null;
    contract_from?: string | null;
    contract_to?: string | null;
  } | null;
}

interface Props {
  isOpen?: boolean;
  shouldFetch?: boolean;
  onFetched?: () => void;
  onApplyFilters: (payload: ReportsFilterPayload) => void;
  onCancel?: () => void;
  dateRange?: [Date | null, Date | null];
  setDateRange?: Dispatch<SetStateAction<[Date | null, Date | null]>>;
  initialFilters?: Partial<ReportsFilterPayload> | null;
}

/** Date input styled like Rewards popup, with clickable custom arrow */
const ForwardedTextInput = forwardRef<HTMLInputElement, any>(
  ({ value, onClick, placeholder, style, sx, ...rest }, ref) => {
    const mergedStyle = { paddingRight: 40, ...(style || {}), width: "100%" };
    return (
      <TextField
        size="small"
        variant="outlined"
        inputRef={ref}
        onClick={onClick}
        value={value}
        placeholder={placeholder || "Select date range"}
        fullWidth
        {...rest}
        sx={{ position: "relative", width: "100%", ...(sx || {}) }}
        InputProps={{
          ...(rest.InputProps || {}),
          endAdornment: (
            <>
              {rest.InputProps?.endAdornment}
              <InputAdornment position="end" sx={{ position: "absolute", right: 10 }}>
                <CustomArrow
                  open={false}
                  onClick={() => {
                    try {
                      onClick && (onClick as unknown as () => void)();
                    } catch {
                      try {
                        onClick?.();
                      } catch {}
                    }
                  }}
                />
              </InputAdornment>
            </>
          ),
        }}
        inputProps={{
          ...(rest.inputProps || {}),
          style: mergedStyle,
        }}
      />
    );
  }
);
ForwardedTextInput.displayName = "ForwardedTextInput";

/** Compact MultiSelect styled like Rewards popup (with Select All, custom arrow, controlled open) */
function CustomMultiSelectWithSearch({
  label,
  options,
  value,
  onChange,
  loading,
  formatOptionLabel,
}: {
  label: string;
  options: string[];
  value: string[];
  onChange: (vals: string[]) => void;
  loading?: boolean;
  formatOptionLabel?: (s: string) => string;
}) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  const fmt = (s: string) => (formatOptionLabel ? formatOptionLabel(s) : s);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearchValue("");
      }
    };
    const onEsc = (e: KeyboardEvent) => e.key === "Escape" && (setOpen(false), setSearchValue(""));
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const filtered = (options || []).filter((opt) =>
    fmt(opt).toLowerCase().includes(searchValue.toLowerCase())
  );

  const allSelected = value.length > 0 && value.length === options.length;
  const displayedValue =
    value.length === 0 ? label : value.length <= 2 ? value.map(fmt).join(", ") : `${value.slice(0, 2).map(fmt).join(", ")}…`;

  const toggleOption = (opt: string) =>
    value.includes(opt) ? onChange(value.filter((v) => v !== opt)) : onChange([...value, opt]);

  const toggleSelectAll = () => (allSelected ? onChange([]) : onChange(options.slice()));

  return (
    <Box ref={containerRef} sx={{ width: "100%", position: "relative", userSelect: "none" }}>
      <TextField
        size="small"
        variant="outlined"
        value={displayedValue}
        onClick={() => setOpen((v) => !v)}
        placeholder={label}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <>
              {loading ? <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} /> : null}
              <InputAdornment position="end" sx={{ cursor: "pointer" }}>
                <CustomArrow open={open} onClick={() => setOpen((v) => !v)} />
              </InputAdornment>
            </>
          ),
          sx: {
            cursor: "pointer",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
        }}
        fullWidth
      />

      {open && (
        <Box
          sx={{
            position: "absolute",
            top: "100%",
            left: 0,
            width: "100%",
            maxHeight: 250,
            overflowY: "auto",
            border: "1px solid #ccc",
            backgroundColor: "#fff",
            zIndex: 3500,
            mt: 0.5,
            borderRadius: 1,
            boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Box sx={{ px: 1, py: 0.5 }}>
            <input
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Search..."
              style={{
                width: "100%",
                padding: "10px 10px",
                borderRadius: 4,
                border: "1px solid #ddd",
                fontSize: "0.9rem",
                marginBottom: 8,
                display: "block",
                marginLeft: "auto",
                marginRight: "auto",
                marginTop: "5px",
              }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            />
          </Box>

          <Box sx={{ px: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1, cursor: "pointer" }} onClick={toggleSelectAll}>
              <Checkbox checked={allSelected} indeterminate={value.length > 0 && !allSelected} />
              <Typography variant="body2">Select All</Typography>
            </Box>

            {filtered.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 1 }}>
                No options found
              </Typography>
            )}

            {filtered.map((opt) => {
              const checked = value.includes(opt);
              return (
                <Box
                  key={opt}
                  sx={{ display: "flex", alignItems: "center", py: 0.5, cursor: "pointer" }}
                  onClick={() => toggleOption(opt)}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <Checkbox checked={checked} />
                  <Typography variant="body2">{fmt(opt)}</Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default function ReportsFilterPopup({
  isOpen = false,
  shouldFetch = false,
  onFetched,
  onApplyFilters,
  onCancel,
  dateRange: _dateRange,        // ignored to keep popup local
  setDateRange: _setDateRange,  // ignored to keep popup local
  initialFilters,
}: Props) {
  const token = getToken();

  // lock body scroll while open (parity with Rewards popup UX)
  useEffect(() => {
    if (!isOpen) return;
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const prev = {
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      overflow: document.body.style.overflow,
    };
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.position = prev.position || "";
      document.body.style.top = prev.top || "";
      document.body.style.left = prev.left || "";
      document.body.style.right = prev.right || "";
      document.body.style.overflow = prev.overflow || "";
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  // options from API
  const [locationOptions, setLocationOptions] = useState<string[]>([]);
  const [outletNameOptions, setOutletNameOptions] = useState<string[]>([]);
  const [pmiOptions, setPmiOptions] = useState<string[]>([]);
  const [statusOptions, setStatusOptions] = useState<string[]>([]);
  const [userSfaOptions, setUserSfaOptions] = useState<string[]>([]);

  // loading flags
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingOutletNames, setLoadingOutletNames] = useState(false);
  const [loadingPmi, setLoadingPmi] = useState(false);
  const [loadingUserSfa, setLoadingUserSfa] = useState(false);

  // local selections
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedPmiEntities, setSelectedPmiEntities] = useState<string[]>([]);
  const [selectedOutletNames, setSelectedOutletNames] = useState<string[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string[]>([]);
  const [selectedUserSfa, setSelectedUserSfa] = useState<string[]>([]);

  // which facet is being edited (facet-aware behavior like TaskTrans)
  const [activeFacet, setActiveFacet] = useState<null | "outlet" | "pmi" | "sfa">(null);

  // contracted since (range used in this popup)
  const [contractSinceRange, setContractSinceRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);

  // abort refs & guards
  const filterAbortRef = useRef<AbortController | null>(null);
  const onFetchedCalledRef = useRef(false);
  const skipLocationEffectRef = useRef(false);

  // only fetch after user actually changes something
  const userInteractedRef = useRef(false);
  const markInteraction = () => { userInteractedRef.current = true; };

  // dedupe by stable key
  const lastFetchKeyRef = useRef<string | null>(null);
  const buildKey = () =>
    JSON.stringify({
      loc: [...selectedLocations].sort(),
      out: [...selectedOutletNames].sort(),
      pmi: [...selectedPmiEntities].sort(),
      sfa: [...selectedUserSfa].sort(),
      st:  [...selectedStatus].sort(),
      from: formatDateForPayload(contractSinceRange[0]),
      to:   formatDateForPayload(contractSinceRange[1]),
    });

  // wrapped setters that mark interaction + set active facet where needed
  const setLoc = (v: string[]) => { markInteraction(); setSelectedLocations(v); };
  const setPmi = (v: string[]) => { markInteraction(); setActiveFacet("pmi"); setSelectedPmiEntities(v); };
  const setOutlet = (v: string[]) => { markInteraction(); setActiveFacet("outlet"); setSelectedOutletNames(v); };
  const setStatusVals = (v: string[]) => { markInteraction(); setSelectedStatus(v); };
  const setSfa = (v: string[]) => { markInteraction(); setActiveFacet("sfa"); setSelectedUserSfa(v); };
  const setDateRangeLocal = (r: [Date | null, Date | null]) => { markInteraction(); setContractSinceRange(r); };

  const uniq = <T,>(arr: T[]) => Array.from(new Set((arr || []).filter(Boolean).map(String)));

  // facet-aware fetch (like TaskTrans)
  const fetchFilterOptions = async () => {
    if (filterAbortRef.current) { try { filterAbortRef.current.abort(); } catch {} }
    const ctrl = new AbortController();
    filterAbortRef.current = ctrl;

    try {
      setLoadingOutletNames(true);
      setLoadingPmi(true);
      setLoadingUserSfa(true);

      const base = {
        locations: selectedLocations.length ? selectedLocations : null,
        outletNames: selectedOutletNames.length ? selectedOutletNames : null,
        pmi_entity_code: selectedPmiEntities.length ? selectedPmiEntities : null,
        status: selectedStatus.length ? selectedStatus : null,
        user_salesforce_id: selectedUserSfa.length ? selectedUserSfa : null,
        lastloginStart: formatDateForPayload(contractSinceRange[0]),
        lastloginEnd: formatDateForPayload(contractSinceRange[1]),
      } as const;

      const callOnce = async (payload: any) =>
        getReportsFilterOptions(payload, token!, ctrl.signal);

      let foMain: any = null;
      let foSecondary: any = null;

      if (activeFacet === "outlet") {
        // 1) Broad OUTLET list (omit outletNames)
        foMain = await callOnce({
          ...base,
          outletNames: null,
        });
        // 2) Narrow PMI/SFA by selected outlets (omit their fields to get compat values)
        foSecondary = await callOnce({
          ...base,
          pmi_entity_code: null,
          user_salesforce_id: null,
        });
      } else if (activeFacet === "pmi") {
        // 1) Broad PMI list (omit pmi)
        foMain = await callOnce({
          ...base,
          pmi_entity_code: null,
        });
        // 2) Narrow OUTLET/SFA by selected PMIs
        foSecondary = await callOnce({
          ...base,
          outletNames: null,
          user_salesforce_id: null,
        });
      } else if (activeFacet === "sfa") {
        // 1) Broad SFA list (omit sfa)
        foMain = await callOnce({
          ...base,
          user_salesforce_id: null,
        });
        // 2) Narrow OUTLET/PMI by selected SFAs
        foSecondary = await callOnce({
          ...base,
          outletNames: null,
          pmi_entity_code: null,
        });
      } else {
        // normal single call (no facet focused)
        foMain = await callOnce(base);
      }

      if (ctrl.signal.aborted) return;

      // parse helper
      const parse = (fo: any) => ({
        locs: Array.isArray(fo?.locations) ? fo.locations : fo?.available_cities || [],
        outlets:
          Array.isArray(fo?.outletNames)
            ? fo.outletNames
            : Array.isArray(fo?.outletPairs) ? fo.outletPairs.map((p: any) => p?.label) : [],
        pmis: Array.isArray(fo?.pmiEntityCodes) ? fo.pmiEntityCodes : fo?.available_pmi_codes || [],
        statuses:
          Array.isArray(fo?.statuses) ? fo.statuses :
          Array.isArray(fo?.userStatuses) ? fo.userStatuses :
          Array.isArray(fo?.status) ? fo.status : [],
        sfa: Array.isArray(fo?.userSalesforceIds) ? fo.userSalesforceIds : fo?.sfaIds || [],
      });

      if (activeFacet === "outlet") {
        const A = parse(foMain);
        const B = parse(foSecondary);
        setLocationOptions(uniq(A.locs));
        setOutletNameOptions(uniq([...(A.outlets || []), ...selectedOutletNames]));
        setPmiOptions(uniq([...(B.pmis || []), ...selectedPmiEntities]));
        setUserSfaOptions(uniq([...(B.sfa || []), ...selectedUserSfa]));
        setStatusOptions(uniq([...(A.statuses || []), ...selectedStatus]));
      } else if (activeFacet === "pmi") {
        const A = parse(foMain);
        const B = parse(foSecondary);
        setLocationOptions(uniq(A.locs));
        setPmiOptions(uniq([...(A.pmis || []), ...selectedPmiEntities]));
        setOutletNameOptions(uniq([...(B.outlets || []), ...selectedOutletNames]));
        setUserSfaOptions(uniq([...(B.sfa || []), ...selectedUserSfa]));
        setStatusOptions(uniq([...(A.statuses || []), ...selectedStatus]));
      } else if (activeFacet === "sfa") {
        const A = parse(foMain);
        const B = parse(foSecondary);
        setLocationOptions(uniq(A.locs));
        setUserSfaOptions(uniq([...(A.sfa || []), ...selectedUserSfa]));
        setOutletNameOptions(uniq([...(B.outlets || []), ...selectedOutletNames]));
        setPmiOptions(uniq([...(B.pmis || []), ...selectedPmiEntities]));
        setStatusOptions(uniq([...(A.statuses || []), ...selectedStatus]));
      } else {
        const A = parse(foMain);
        setLocationOptions(uniq(A.locs));
        setOutletNameOptions(uniq([...(A.outlets || []), ...selectedOutletNames]));
        setPmiOptions(uniq([...(A.pmis || []), ...selectedPmiEntities]));
        setUserSfaOptions(uniq([...(A.sfa || []), ...selectedUserSfa]));
        setStatusOptions(uniq([...(A.statuses || []), ...selectedStatus]));
      }

      // ✅ Do NOT prune current selections here
    } catch (err: any) {
      if (err?.name !== "AbortError") console.error("Error fetching report filter options:", err);
    } finally {
      if (filterAbortRef.current === ctrl) filterAbortRef.current = null;
      setLoadingOutletNames(false);
      setLoadingPmi(false);
      setLoadingUserSfa(false);
      setActiveFacet(null);
    }
  };

  // initial fetch (when parent says to fetch & popup open)
  useEffect(() => {
    if (!isOpen || !shouldFetch) return;
    let cancelled = false;

    const fetchLists = async () => {
      try {
        setLoadingLocations(true);

        const [locRes, otRes] = await Promise.allSettled([getalllocations?.(), getalloutlettype?.()]);

        if (cancelled) return;

        if (locRes.status === "fulfilled") {
          const locVal: any = locRes.value;
          const arr =
            (Array.isArray(locVal?.data?.data) && locVal.data.data) ||
            (Array.isArray(locVal?.data) && locVal.data) ||
            (Array.isArray(locVal?.Data) && locVal.Data) ||
            (Array.isArray(locVal) && locVal) ||
            [];
          const locs = arr.map((s: any) => String(s ?? "").trim()).filter(Boolean);
          setLocationOptions(locs);
        } else {
          console.error("getalllocations failed:", locRes.reason);
          setLocationOptions([]);
        }

        // base fetch once
        await fetchFilterOptions();

        if (!cancelled && !onFetchedCalledRef.current) {
          onFetchedCalledRef.current = true;
          onFetched?.();
        }
      } catch (err: any) {
        if (err?.name !== "AbortError") {
          console.error("Error loading initial report filter lists:", err);
        }
      } finally {
        if (!cancelled) setLoadingLocations(false);
      }
    };

    fetchLists();

    return () => {
      cancelled = true;
      if (filterAbortRef.current) {
        try {
          filterAbortRef.current.abort();
        } catch {}
        filterAbortRef.current = null;
      }
      onFetchedCalledRef.current = false;
      lastFetchKeyRef.current = null; // reset dedupe key on close/unmount
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, shouldFetch]);

  // dependent fetch whenever any narrowing input changes — ONLY after user interaction
  useEffect(() => {
    if (!isOpen) return;
    if (skipLocationEffectRef.current) {
      setLoadingOutletNames(false);
      setLoadingPmi(false);
      setLoadingUserSfa(false);
      return;
    }
    if (!userInteractedRef.current) return;

    const key = buildKey();
    if (lastFetchKeyRef.current === key) return; // prevent duplicate loop
    lastFetchKeyRef.current = key;

    fetchFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedLocations,
    selectedOutletNames,
    selectedPmiEntities,
    selectedUserSfa,
    selectedStatus,
    contractSinceRange,
    isOpen,
  ]);

  // cleanup on unmount
  useEffect(() => {
    return () => {
      if (filterAbortRef.current) {
        try {
          filterAbortRef.current.abort();
        } catch {}
        filterAbortRef.current = null;
      }
    };
  }, []);

  // rehydrate from initialFilters (once provided)
  useEffect(() => {
    if (!initialFilters) return;

    setSelectedLocations(initialFilters.locations ?? []);
    setSelectedPmiEntities(initialFilters.pmi_entity_code ?? []);
    setSelectedOutletNames(initialFilters.outletNames ?? []);
    setSelectedStatus(initialFilters.status ?? []);
    const sfaInit =
    (initialFilters as any).user_salesforce_id ??
    (initialFilters as any).userSalesforceId ??
    [];
  setSelectedUserSfa(Array.isArray(sfaInit) ? sfaInit : sfaInit ? [String(sfaInit)] : []);

    const fromStr =
      (initialFilters as any).lastloginStart ?? initialFilters.contract_since_from ?? null;
    const toStr =
      (initialFilters as any).lastloginEnd ?? initialFilters.contract_since_to ?? null;

    const from = fromStr ? new Date(fromStr) : null;
    const to = toStr ? new Date(toStr) : null;

    setContractSinceRange([from, to]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilters]);

  const btnResetStyle: React.CSSProperties = {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    padding: 4,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const renderHeaderWithIcons = ({
    date,
    decreaseMonth,
    increaseMonth,
    decreaseYear,
    increaseYear,
  }: {
    date: Date;
    decreaseMonth: () => void;
    increaseMonth: () => void;
    decreaseYear: () => void;
    increaseYear: () => void;
  }) => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
      <button type="button" onClick={decreaseYear} aria-label="Previous year" style={btnResetStyle}>
        <img src={doubleArrowLeft} alt="Previous year" />
      </button>
      <button type="button" onClick={decreaseMonth} aria-label="Previous month" style={btnResetStyle}>
        <img src={arrowLeft} alt="Previous month" />
      </button>

      <span style={{ margin: "0 10px", fontWeight: 600 }}>
        {date.toLocaleString("default", { year: "numeric", month: "long" })}
      </span>

      <button type="button" onClick={increaseMonth} aria-label="Next month" style={btnResetStyle}>
        <img src={arrowRight} alt="Next month" />
      </button>
      <button type="button" onClick={increaseYear} aria-label="Next year" style={btnResetStyle}>
        <img src={doubleArrowRight} alt="Next year" />
      </button>
    </div>
  );

  const formatDateForPayload = (d: Date | null): string | null => {
    if (!d) return null;
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const clearAll = () => {
    skipLocationEffectRef.current = true;
    userInteractedRef.current = false;
    lastFetchKeyRef.current = null;

    setSelectedLocations([]);
    setSelectedPmiEntities([]);
    setSelectedOutletNames([]);
    setSelectedStatus([]);
    setSelectedUserSfa([]);
    setContractSinceRange([null, null]);
    _setDateRange?.([null, null]);

    const payload: ReportsFilterPayload = {
      locations: null,
      pmi_entity_code: null,
      outletNames: null,
      status: null,
      user_salesforce_id: null,
      lastloginStart: null,
      lastloginEnd: null,
      backendFilters: {
        outlet_city: null,
        outlet_category: null,
      },
    };

    try {
      onApplyFilters(payload);
    } catch (e) {
      console.error("onApplyFilters threw during clearAll:", e);
    }

    setTimeout(() => {
      skipLocationEffectRef.current = false;
      try {
        onCancel?.();
      } catch (e) {
        console.error("onCancel threw during clearAll:", e);
      }
    }, 0);
  };

  const handleApply = () => {
    const payload: ReportsFilterPayload = {
      locations: selectedLocations.length > 0 ? selectedLocations.slice() : null,
      pmi_entity_code: selectedPmiEntities.length > 0 ? selectedPmiEntities.slice() : null,
      outletNames: selectedOutletNames.length > 0 ? selectedOutletNames.slice() : null,
      status: selectedStatus.length > 0 ? selectedStatus.slice() : null,
      user_salesforce_id: selectedUserSfa.length > 0 ? selectedUserSfa.slice() : null,
      lastloginStart: formatDateForPayload(contractSinceRange[0]),
      lastloginEnd:   formatDateForPayload(contractSinceRange[1]),
      backendFilters: {
        outlet_city: selectedLocations.length > 0 ? selectedLocations.slice() : null,
        outlet_category: null,
      },
    };
    _setDateRange?.([
      contractSinceRange[0] ?? null,
      contractSinceRange[1] ?? null,
    ]);

    onApplyFilters(payload);
  };

  const formatRangeText = (range: [Date | null, Date | null]) => {
    if (!range || (!range[0] && !range[1])) return "Select date range";
    const s = range[0] ? range[0].toLocaleDateString() : "";
    const e = range[1] ? range[1].toLocaleDateString() : "";
    return s && e ? `${s} - ${e}` : s || e || "Select date range";
  };

  // simple Title Case for status display only
  const toTitle = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s);

  return (
    <Box
      sx={{
        width: 420,
        maxWidth: "calc(100vw - 32px)",
        maxHeight: "80vh",
        background: "#fff",
        borderRadius: 2,
        p: 0,
        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ p: 2.5, borderBottom: "1px solid #eee" }}>
        <Typography sx={{ fontWeight: 600 }}>Apply Filters</Typography>
      </Box>

      <GlobalStyles
        styles={{
          '.MuiAutocomplete-option[aria-selected="true"]': { backgroundColor: "transparent !important" },
          ".MuiAutocomplete-option.Mui-focused": { backgroundColor: "transparent !important" },
          '.MuiAutocomplete-option[aria-selected="true"].Mui-focused': { backgroundColor: "transparent !important" },
          ".react-datepicker-wrapper": { width: "100% !important" },
        }}
      />

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 2.5,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Select Locations</Typography>
          <CustomMultiSelectWithSearch
            label="Select Locations"
            options={locationOptions}
            value={selectedLocations}
            onChange={setLoc}
            loading={loadingLocations}
          />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Last Updated On</Typography>
          <Box sx={{ width: "100%" }}>
            <GlobalStyles styles={{ ".react-datepicker-wrapper": { width: "100% !important" } }} />
            <DatePicker
              selectsRange
              startDate={contractSinceRange[0] ?? undefined}
              endDate={contractSinceRange[1] ?? undefined}
              onChange={(range: [Date | null, Date | null]) => setDateRangeLocal(range)}
              dateFormat="yyyy-MM-dd"
              renderCustomHeader={renderHeaderWithIcons}
              customInput={
                <ForwardedTextInput
                  placeholder={formatRangeText(contractSinceRange)}
                  key={`contract-${contractSinceRange[0]?.getTime() ?? 0}-${contractSinceRange[1]?.getTime() ?? 0}`}
                  InputProps={{
                    endAdornment:
                      contractSinceRange[0] || contractSinceRange[1] ? (
                        <img
                          onClick={(e) => {
                            e.stopPropagation();
                            setDateRangeLocal([null, null]);
                          }}
                          src={CLEAR_X_DATA_URI}
                          alt="Clear"
                          style={{
                            height: 18,
                            width: 18,
                            cursor: "pointer",
                            marginRight: 10,
                            paddingTop: "4px",
                          }}
                        />
                      ) : undefined,
                  }}
                />
              }
            />
          </Box>
        </Box>
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>User Salesforce ID</Typography>
          <CustomMultiSelectWithSearch
            label="User Salesforce ID"
            options={userSfaOptions}
            value={selectedUserSfa}
            onChange={setSfa}
            loading={loadingUserSfa}
          />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Status</Typography>
          <CustomMultiSelectWithSearch
            label="Status"
            options={statusOptions}
            value={selectedStatus}
            onChange={setStatusVals}
            loading={false}
            formatOptionLabel={toTitle}
          />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Select PMI Entity</Typography>
          <CustomMultiSelectWithSearch
            label="Select PMI Entity"
            options={pmiOptions}
            value={selectedPmiEntities}
            onChange={setPmi}
            loading={loadingPmi}
          />
        </Box>

        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Select Outlet Names</Typography>
          <CustomMultiSelectWithSearch
            label="Select Outlet Names"
            options={outletNameOptions}
            value={selectedOutletNames}
            onChange={setOutlet}
            loading={loadingOutletNames}
          />
        </Box>

      </Box>

      <Box
        sx={{
          p: 2.5,
          borderTop: "1px solid #eee",
          display: "flex",
          justifyContent: "flex-end",
          gap: 1.25,
        }}
      >
        <Button
          onClick={clearAll}
          sx={{ border: "1px solid #E7E8E9", color: "#000", borderRadius: 2, height: 40 }}
        >
          CLEAR ALL
        </Button>
        <Button
          onClick={handleApply}
          sx={{ border: "1px solid #C8172F", color: "#C8172F", borderRadius: 2, height: 40 }}
        >
          APPLY
        </Button>
      </Box>
    </Box>
  );
}
